/**
 * serverDisplay -- Centralized server-type display helpers.
 *
 * Provides label, brand color, icon, and Tailwind color class lookups for
 * all supported media server types (Emby, Jellyfin, Plex). Eliminates the
 * ~57 scattered `serverType === "emby" ? ... : "Jellyfin"` ternaries across
 * WSD components and ensures Plex renders correctly everywhere.
 *
 * Consumers:
 *   - All streaming route pages (MediaItemPage, DevicesPage, UserDetailPage)
 *   - All streaming components (SessionCard, UserAdminPanel, UserDrawer, etc.)
 *   - Control-center domains and media sub-components
 */

// ============================================================================
// Types
// ============================================================================

export type ServerTypeForDisplay = "emby" | "jellyfin" | "plex";

// ============================================================================
// Label map
// ============================================================================

export const SERVER_LABELS: Record<ServerTypeForDisplay, string> = {
  emby: "Emby",
  jellyfin: "Jellyfin",
  plex: "Plex",
};

// ============================================================================
// Brand color map (hex, for inline styles / canvas)
// ============================================================================

export const SERVER_BRAND_COLORS: Record<ServerTypeForDisplay, string> = {
  emby: "#52b54b",
  jellyfin: "#aa5cc3",
  plex: "#e5a00d",
};

// ============================================================================
// Iconify icon name map
// ============================================================================

export const SERVER_ICONS: Record<ServerTypeForDisplay, string> = {
  emby: "simple-icons:emby",
  jellyfin: "simple-icons:jellyfin",
  // simple-icons:plex is available in the icon set used by the project
  plex: "simple-icons:plex",
};

// ============================================================================
// Tailwind text-color class map
// These match the existing Emby (emerald) and Jellyfin (purple) conventions.
// Plex uses amber to match the #e5a00d brand color.
// ============================================================================

export const SERVER_COLOR_CLASSES: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-500",
  jellyfin: "text-purple-500",
  plex: "text-amber-500",
};

/** Slightly dimmer variant for secondary / decorative icon usage */
export const SERVER_COLOR_CLASSES_MUTED: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-400",
  jellyfin: "text-purple-400",
  plex: "text-amber-400",
};

/** Dimmed variant for 50-70% opacity contexts (e.g., source-list entries) */
export const SERVER_COLOR_CLASSES_DIM: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-500/70",
  jellyfin: "text-purple-500/70",
  plex: "text-amber-500/70",
};

/** 60% dimmed variant used in item panels */
export const SERVER_COLOR_CLASSES_60: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-400/60",
  jellyfin: "text-purple-400/60",
  plex: "text-amber-400/60",
};

/** 50% dimmed variant used in library shelf entries */
export const SERVER_COLOR_CLASSES_50: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-400/50",
  jellyfin: "text-purple-400/50",
  plex: "text-amber-400/50",
};

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Get the display label for a server type.
 * Falls back to the raw value if not in the registry (forward-compat).
 */
export function getServerLabel(serverType: string | undefined | null): string {
  if (!serverType) return "Unknown";
  return SERVER_LABELS[serverType as ServerTypeForDisplay] ?? serverType;
}

/**
 * Get the brand hex color for a server type (for inline styles, canvas, etc.).
 */
export function getServerBrandColor(serverType: string | undefined | null): string {
  if (!serverType) return "#888";
  return SERVER_BRAND_COLORS[serverType as ServerTypeForDisplay] ?? "#888";
}

/**
 * Get the Iconify icon name for a server type.
 */
export function getServerIcon(serverType: string | undefined | null): string {
  if (!serverType) return "lucide:server";
  return SERVER_ICONS[serverType as ServerTypeForDisplay] ?? "lucide:server";
}

/**
 * Get the Tailwind text-color class for a server type (full / primary usage).
 */
export function getServerColorClass(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the muted Tailwind text-color class (decorative / secondary icon usage).
 */
export function getServerColorClassMuted(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_MUTED[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the dimmed (70%) Tailwind text-color class.
 */
export function getServerColorClassDim(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_DIM[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the 60%-opacity Tailwind text-color class (used in item device panels).
 */
export function getServerColorClass60(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_60[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the 50%-opacity Tailwind text-color class (used in library shelf entries).
 */
export function getServerColorClass50(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_50[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

// ============================================================================
// Avatar classes (combined `bg-X-500/15 text-X-400` for avatar circles)
// ============================================================================

const SERVER_AVATAR_CLASSES: Record<ServerTypeForDisplay, string> = {
  emby: "bg-emerald-500/15 text-emerald-400",
  jellyfin: "bg-purple-500/15 text-purple-400",
  plex: "bg-amber-500/15 text-amber-400",
};

/**
 * Get the combined avatar Tailwind classes (background + text) for a server type.
 * Used for user avatar circles and similar paired bg/text elements.
 */
export function getServerAvatarClasses(serverType: string | undefined | null): string {
  if (!serverType) return "bg-muted text-muted-foreground";
  return SERVER_AVATAR_CLASSES[serverType as ServerTypeForDisplay] ?? "bg-muted text-muted-foreground";
}

// ============================================================================
// Background color map (for server badge circles, etc.)
// ============================================================================

const SERVER_BG_CLASSES: Record<ServerTypeForDisplay, string> = {
  emby: "bg-emerald-500",
  jellyfin: "bg-purple-500",
  plex: "bg-amber-500",
};

/**
 * Get the Tailwind background-color class for a server type.
 * Used for server badge circles, avatar backgrounds, etc.
 */
export function getServerBgClass(serverType: string | undefined | null): string {
  if (!serverType) return "bg-muted";
  return SERVER_BG_CLASSES[serverType as ServerTypeForDisplay] ?? "bg-muted";
}

// ============================================================================
// Deep-link URL builders
// ============================================================================

/**
 * Parse the leading `major[.minor]` of a server version string.
 * Tolerates a `v` prefix and trailing build metadata ("10.9.0", "v12.1",
 * "10.10.7-beta"). Returns null when no leading number is present.
 */
function parseServerVersion(
  version: string | undefined | null,
): { major: number; minor: number } | null {
  if (!version) return null;
  const match = /^\s*v?(\d+)(?:\.(\d+))?/.exec(version);
  if (!match) return null;
  const major = Number(match[1]);
  const minor = match[2] != null ? Number(match[2]) : 0;
  if (!Number.isFinite(major) || !Number.isFinite(minor)) return null;
  return { major, minor };
}

/**
 * Whether a Jellyfin version uses the legacy hashbang (`#!`) router for item
 * routes. Only jellyfin-web 10.8 and earlier do; 10.9+ switched to `createHashRouter`
 * (`#/`), and 10.9.0 ships no `#!` redirect, so an unknown version defaults to the
 * modern `#/` (the safer guess).
 */
function jellyfinUsesLegacyItemHash(version: string | undefined | null): boolean {
  const v = parseServerVersion(version);
  if (!v) return false;
  if (v.major < 10) return true;
  return v.major === 10 && v.minor < 9;
}

/**
 * Build the deep-link URL into a server's web UI for a specific item.
 *
 * - Emby:     `{base}/web/index.html#!/item?id={itemId}&serverId={serverId}`
 * - Jellyfin: `{base}/web/#/details?id={itemId}&serverId={serverId}` (10.9+ /
 *             unknown; `#!/details` on 10.8 and earlier)
 * - Plex:     `{base}/web/index.html#!/server/{machineId}/details?key={encoded /library/metadata/itemId}`
 *
 * Also used to open a person (cast/crew member): on Emby/Jellyfin a person is an
 * item, so the person id is a valid `itemId`. Plex person ids are tag ids, not
 * item ids — callers must not pass them here.
 *
 * @param serverType    - The media server type ("emby" | "jellyfin" | "plex")
 * @param baseUrl       - The server base URL (no trailing slash)
 * @param itemId        - The item's ID on the media server
 * @param serverId      - For Emby/Jellyfin: the `serverId` query param;
 *                        for Plex: the machine identifier
 * @param serverVersion - Optional Jellyfin version; selects the hash router form
 */
export function buildServerItemUrl(args: {
  serverType: string | undefined | null;
  baseUrl: string;
  itemId: string;
  serverId?: string | null;
  serverVersion?: string | null;
}): string | null {
  const { serverType, baseUrl, itemId, serverId, serverVersion } = args;
  if (!serverType || !baseUrl || !itemId) return null;

  const base = baseUrl.replace(/\/+$/, "");

  if (serverType === "plex") {
    // Plex deep-link requires the machine identifier in the path.
    // serverId carries the machineIdentifier populated by the Plex adapter.
    if (!serverId) return null;
    const key = encodeURIComponent(`/library/metadata/${itemId}`);
    return `${base}/web/index.html#!/server/${serverId}/details?key=${key}`;
  }

  const serverIdParam = serverId ? `&serverId=${encodeURIComponent(serverId)}` : "";

  if (serverType === "emby") {
    return `${base}/web/index.html#!/item?id=${encodeURIComponent(itemId)}${serverIdParam}`;
  }

  if (serverType === "jellyfin") {
    const hash = jellyfinUsesLegacyItemHash(serverVersion) ? "#!" : "#";
    return `${base}/web/${hash}/details?id=${encodeURIComponent(itemId)}${serverIdParam}`;
  }

  return null;
}

/**
 * Build the deep-link URL into a server's admin page for a specific user.
 *
 * - Emby:     `{base}/web/index.html#!/users/user?userId={userId}`
 * - Jellyfin: `{base}/web/#/dashboard/users/{userId}/profile` on v12+,
 *             `{base}/web/#/dashboard/users/profile?userId={userId}` on 10.x.
 *             The route differs by major line and there is no shared form, so an
 *             unknown version returns null rather than guessing a broken link.
 * - Plex:     no per-server user page — returns null.
 */
export function buildServerUserUrl(args: {
  serverType: string | undefined | null;
  baseUrl: string;
  userId: string;
  serverVersion?: string | null;
}): string | null {
  const { serverType, baseUrl, userId, serverVersion } = args;
  if (!serverType || !baseUrl || !userId) return null;

  const base = baseUrl.replace(/\/+$/, "");
  const id = encodeURIComponent(userId);

  if (serverType === "emby") {
    return `${base}/web/index.html#!/users/user?userId=${id}`;
  }

  if (serverType === "jellyfin") {
    const v = parseServerVersion(serverVersion);
    if (!v) return null;
    if (v.major >= 12) return `${base}/web/#/dashboard/users/${id}/profile`;
    return `${base}/web/#/dashboard/users/profile?userId=${id}`;
  }

  // Plex has no per-server user page.
  return null;
}

/**
 * Build the deep-link URL into a server's admin page for a specific device.
 *
 * - Emby:     `{base}/web/index.html#!/devices/device.html?id={deviceId}`
 * - Jellyfin: `{base}/web/#/dashboard/devices/edit?id={deviceId}` on 10.x;
 *             v12 dropped the per-device page, so it returns the devices list
 *             `{base}/web/#/dashboard/devices`. Unknown version returns null.
 * - Plex:     no per-server device page — returns null.
 */
export function buildServerDeviceUrl(args: {
  serverType: string | undefined | null;
  baseUrl: string;
  deviceId: string;
  serverVersion?: string | null;
}): string | null {
  const { serverType, baseUrl, deviceId, serverVersion } = args;
  if (!serverType || !baseUrl) return null;

  const base = baseUrl.replace(/\/+$/, "");

  if (serverType === "emby") {
    if (!deviceId) return null;
    return `${base}/web/index.html#!/devices/device.html?id=${encodeURIComponent(deviceId)}`;
  }

  if (serverType === "jellyfin") {
    const v = parseServerVersion(serverVersion);
    if (!v) return null;
    // v12 has no per-device page — the devices list is the closest target.
    if (v.major >= 12) return `${base}/web/#/dashboard/devices`;
    if (!deviceId) return null;
    return `${base}/web/#/dashboard/devices/edit?id=${encodeURIComponent(deviceId)}`;
  }

  // Plex has no per-server device page.
  return null;
}

// ============================================================================
// Version selection (feeds the deep-link builders above)
// ============================================================================

/**
 * Pick the reported web-UI version for one server type out of a ping result set,
 * for the `serverVersion` argument of the deep-link builders above.
 *
 * Returns null when there is no successful ping for that type or the ping carried
 * no version, so a builder falls back to its version-agnostic default (today's
 * behaviour: Emby links unchanged, Jellyfin item default `#/details`, Jellyfin
 * user/device hidden). Accepts the minimal `{ serverType, version, ok }` shape so
 * it is independent of the ping hook's full result type.
 */
export function selectServerVersion(
  servers: ReadonlyArray<{ serverType: string; version: string | null; ok?: boolean }>,
  serverType: string | undefined | null,
): string | null {
  if (!serverType) return null;
  const match = servers.find((s) => s.serverType === serverType);
  if (!match || match.ok === false) return null;
  return match.version ?? null;
}
